import { useMemo } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@/contexts/auth-context'
import { useOnboardingRequirements } from '@/hooks/useOnboardingRequirements'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { getResumeDestination } from '@/utils/getResumeDestination'
import { LoadingIcon } from './LoadingIcon'

const SIGNUP_PATH_PREFIX = '/Signup'
const UPLOAD_PATH = '/Upload'
const IN_REVIEW_PATH = '/Complete'

const PASSTHROUGH_PATHS = new Set([
  '/EasyLogin',
  '/LandingLogin',
  '/Login',
  '/KeyLogin',
  '/LoginVerify',
  '/Complete',
  '/Failed',
  '/Upload',
])

export const OnboardingGate = () => {
  const { isAuth, loading: isAuthLoading } = useAuth()
  const location = useLocation()

  const {
    data: requirements,
    isLoading: isRequirementsLoading,
    isError: requirementsError,
    refetch: refetchRequirements,
  } = useOnboardingRequirements(isAuth)

  const {
    data: currentUser,
    isLoading: isUserLoading,
    isError: currentUserError,
    refetch: refetchCurrentUser,
  } = useCurrentUser(isAuth)

  const isLoading = isAuthLoading || isRequirementsLoading || isUserLoading

  const destination = useMemo(
    () =>
      requirements && currentUser
        ? getResumeDestination(requirements, currentUser.status)
        : null,
    [requirements, currentUser]
  )

  if (PASSTHROUGH_PATHS.has(location.pathname)) {
    return <Outlet />
  }

  if (isLoading) {
    return (
      <div className="my-auto flex justify-center">
        <LoadingIcon />
      </div>
    )
  }

  if (!isAuth) {
    return <Outlet />
  }

  if (requirementsError || currentUserError) {
    return (
      <div
        className="my-auto flex flex-col items-center justify-center gap-4 p-5 text-center"
        data-testid="container-onboardinggate-error"
      >
        <p className="text-base">
          We couldn&apos;t load your account state. Please try again.
        </p>
        <button
          type="button"
          onClick={() => {
            refetchRequirements()
            refetchCurrentUser()
          }}
          className="underline"
          data-testid="btn-onboardinggate-retry"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (destination?.type === 'upload' && location.pathname !== UPLOAD_PATH) {
    return <Navigate to={UPLOAD_PATH} replace />
  }

  if (
    destination?.type === 'in-review' &&
    location.pathname !== IN_REVIEW_PATH
  ) {
    return <Navigate to={IN_REVIEW_PATH} replace />
  }

  if (
    destination?.type === 'signup' &&
    !location.pathname.startsWith(SIGNUP_PATH_PREFIX)
  ) {
    return <Navigate to={destination.path} replace />
  }

  if (
    destination === null &&
    location.pathname.startsWith(SIGNUP_PATH_PREFIX)
  ) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
